import React, { useEffect, useRef, useState } from 'react';
import { UseModalFeatureParams, UseModalFeatureReturn } from './types';

/**
 * useModalFeature
 *
 * Encapsulates all interaction and lifecycle behavior for the `Modal` component,
 * keeping rendering concerns in the UI layer and business/interaction logic in
 * the feature hook layer.
 *
 * Responsibilities handled by this hook:
 * - Mount-state tracking for portal-safe rendering
 * - Optional close-button autofocus on open
 * - Escape-key handling for keyboard dismissal
 * - Backdrop-click close behavior
 * - Background scroll lock while modal is open
 *
 * @param autoFocus - Enables focus transfer to the close button when opened.
 * @param closeOnBackdropClick - Enables clicking outside panel to close.
 * @param closeOnEscape - Enables Escape key to close.
 * @param isOpen - Current open state of the modal.
 * @param onClose - Callback invoked when modal should close.
 *
 * @returns refs and handlers consumed by `Modal`.
 */
export const useModalFeature = ({
  autoFocus,
  closeOnBackdropClick,
  closeOnEscape,
  isOpen,
  onClose,
}: UseModalFeatureParams): UseModalFeatureReturn => {
  // ================================================================
  // Refs
  // ================================================================

  /**
   * Ref for the close button.
   * Used by the autofocus effect to place keyboard focus on open.
   */
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Ref for the modal panel element.
   * Used to determine whether a click happened inside or outside the panel.
   */
  const modalRef = useRef<HTMLDivElement>(null);

  // ================================================================
  // State
  // ================================================================

  /**
   * Tracks whether the hook owner has mounted on the client.
   * This is useful for portal rendering to avoid hydration mismatches.
   */
  const [mounted, setMounted] = useState(false);

  // ================================================================
  // Effect: mount lifecycle
  // ================================================================

  /**
   * Set mount state once after first render.
   */
  useEffect(() => {
    setMounted(true);
  }, []);

  // ================================================================
  // Effect: focus management
  // ================================================================

  /**
   * Moves focus to the close button when all required conditions are met:
   * - `autoFocus` is enabled
   * - modal is open
   * - component is mounted
   * - close button ref exists
   */
  useEffect(() => {
    const shouldFocus = autoFocus && isOpen && mounted && closeButtonRef.current;

    if (shouldFocus) {
      closeButtonRef.current.focus();
    }
  }, [autoFocus, isOpen, mounted]);

  // ================================================================
  // Effect: keyboard dismissal (Escape)
  // ================================================================

  /**
   * Registers an Escape key listener only while the modal is open and
   * keyboard-close behavior is enabled.
   *
   * Listener is properly cleaned up whenever dependencies change or on unmount.
   */
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // ================================================================
  // Effect: body scroll lock
  // ================================================================

  /**
   * Prevents background page scroll while modal is open by toggling
   * `document.body.style.overflow`.
   *
   * Cleanup always restores overflow to `unset` for safety.
   */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ================================================================
  // Event handlers
  // ================================================================

  /**
   * Handles clicks on the backdrop container.
   *
   * Close behavior occurs only when:
   * - backdrop click closing is enabled
   * - modal element exists
   * - click target is outside the modal panel
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (!closeOnBackdropClick) return;

    const modalElement = modalRef.current;
    const target = event.target as Node | null;
    const clickedInsideModal = modalElement?.contains(target);

    if (modalElement && !clickedInsideModal) {
      onClose();
    }
  };

  // ================================================================
  // Hook return API
  // ================================================================

  return {
    closeButtonRef,
    handleBackdropClick,
    modalRef,
    mounted,
  };
};

export default useModalFeature;
